import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout, Row, Col } from 'antd';
import {
  Card,
  Button,
  Select,
  TextField,
  SnapDialog
} from '@one-snap/sparkles';
import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Snackbar
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Navbar from '../stores/Navbar';
import './ProductStyles.css';
import Checkout from '../sdk/Checkout';
import LtoCheckout from '../lto/LtoCheckout';

const { Content } = Layout;

const autoProductData = [
  {
    id: 1,
    name: 'Wheels',
    fancyName: 'Eagle Exhilarate',
    description: '$220.99',
    brand: 'Good Year',
    imageUrl: '/sdk-wheel.jpg',
    style: { maxWidth: '68%' }
  },
  {
    id: 2,
    name: 'Tires',
    fancyName: 'Roadian GTX',
    description: '$160.99',
    brand: 'Nexen',
    imageUrl: '/sdk-tyre.jpg',
    style: { maxWidth: '50%' }
  },
  {
    id: 3,
    name: 'Rims',
    brand: 'MOTO METAL MO992 FOLSOM',
    description: '$150.99',
    fancyName: 'Chrome',
    imageUrl: '/sdk-carrims.jpg',
    style: { maxWidth: '70%' }
  },
  {
    id: 4,
    name: 'Wheels',
    fancyName: 'Glide',
    description: '$220.99',
    brand: 'UNO MINDA',
    imageUrl: '/tires-2.jpg',
    style: { maxWidth: '65%' }
  },
  {
    id: 5,
    name: 'Tires',
    fancyName: 'Select',
    description: '$160.99',
    brand: 'Bridgestone',
    imageUrl: '/tires-3.jpg',
    style: { maxWidth: '65%' }
  },
  {
    id: 6,
    name: 'Rims',
    brand: 'MRF',
    description: '$150.99',
    fancyName: 'Perfinza CLX1',
    imageUrl: '/tires-4.webp',
    style: { maxWidth: '65%' }
  }
];

const furnitureProductData = [
  {
    id: 1,
    name: 'Bed',
    fancyName: 'Upholstered Bed',
    brand: 'Home Centre',
    description: '$220.99',
    imageUrl: '/sdk-bed.jpg',
    style: { maxWidth: '100%' }
  },
  {
    id: 2,
    name: 'Dining Table',
    description: '$160.99',
    fancyName: 'Dining Table',
    brand: 'Urban Ladder',
    imageUrl: '/sdk-diningset.jpg',
    style: { maxWidth: '100%' }
  },
  {
    id: 3,
    name: 'Sofa',
    description: '$150.99',
    fancyName: 'Apollo Sofa',
    brand: 'Home Center',
    imageUrl: '/sdk-sofa.jpg',
    style: { maxWidth: '100%' }
  },
  {
    id: 4,
    name: 'Upholstered Sofa',
    fancyName: 'Upholstered Sofa',
    brand: 'Home Centre',
    description: '$220.99',
    imageUrl: '/sofa-1.JPG',
    style: { maxWidth: '100%' }
  },
  {
    id: 5,
    name: 'Accent Chair',
    description: '$160.99',
    fancyName: 'Ormond Accent Chair',
    brand: 'Urban Ladder',
    imageUrl: '/accent-chair.JPG',
    style: { maxWidth: '105%' }
  },
  {
    id: 6,
    name: 'Mattress',
    description: '$150.99',
    fancyName: 'SimplyWud Mattress',
    brand: 'Home Center',
    imageUrl: '/matress.JPG',
    style: { maxWidth: '103%' }
  }
];

// eslint-disable-next-line react/prop-types
function ProductsPage({ storeType }) {
  const location = useLocation();
  const { env, audience, token } = location.state;

  if (!env || !token || !storeType) return null;

  const [cart, setCart] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState('auto');
  const [quantities, setQuantities] = useState({});
  const [showInfo, setShowInfo] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const salesTax = 15;
  const discount = 15;
  const shipping = 20;
  const currency = 'USD';

  const handleSelectContentChange = (event) => {
    setSelectedContentType(event.target.value);
    setCart([]);
  };

  const handleInfoClose = () => {
    setShowInfo(false);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleOpenSnackbar = () => {
    setOpenSnackbar(true);
  };

  const handleQuantityChange = (productId, quantity) => {
    setQuantities((prev) => ({ ...prev, [productId]: quantity }));
  };

  const getContentData = () =>
    selectedContentType === 'auto' ? autoProductData : furnitureProductData;

  const addToCart = (product) => {
    const quantity = quantities[product.id] || 1; // Use selected quantity or default to 1
    const existingProduct = cart.find((item) => item.id === product.id);

    if (existingProduct) {
      setCart((prevCart) =>
        prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        )
      );
    } else {
      setCart((prevCart) => [...prevCart, { ...product, quantity }]);
    }

    // Reset the quantity after adding to cart
    setQuantities((prev) => ({ ...prev, [product.id]: 1 }));
  };

  const incrementQuantity = (productId) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const decrementQuantity = (productId) => {
    setCart((prevCart) => {
      const updatedCart = prevCart.map((item) =>
        item.id === productId ? { ...item, quantity: item.quantity - 1 } : item
      );
      return updatedCart.filter((item) => item.quantity > 0);
    });
  };

  const contentTypes = [
    { id: 1, name: 'auto', displayText: 'Auto Services' },
    { id: 2, name: 'furniture', displayText: 'Furniture' }
  ];

  const storyBookStyles = {
    width: '100%',
    maxWidth: '300px',
    marginBottom: '20px'
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <Layout id="productsPageLayout">
      <Navbar
        audience={audience}
        env={env}
        token={token}
        isHomeScreen={false}
      />
      <Content
        id="contentContainer"
        className="sdkProductsContainer"
        style={{
          padding: '30px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          minHeight: '86vh',
          flexWrap: 'wrap',
          marginTop: '70px'
        }}
      >
        <div id="cardContainer" className="store-form-control">
          <FormControl style={storyBookStyles}>
            <InputLabel>Select the Category</InputLabel>
            <Select
              label="Select the Industry"
              value={selectedContentType}
              onChange={handleSelectContentChange}
              style={storyBookStyles}
              className="store-category-selection"
            >
              {contentTypes.map((content) => (
                <MenuItem key={content.id} value={content.name}>
                  {content.displayText}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Row>
            <Col span={16}>
              <Row gutter={13}>
                {getContentData().map((product) => (
                  <Col key={product.id} span={8}>
                    <Card
                      id={`productCard_${product.id}`}
                      className="store-item-card"
                      borderOnHover={false}
                      insetBorder={false}
                    >
                      <div className="store-item-brand">{product.brand}</div>
                      <div className="store-product-fancy-name">
                        {product.fancyName}
                      </div>
                      <div
                        className="image-container"
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          style={product.style}
                          className="store-product-image"
                        />
                      </div>
                      <div
                        id={`productDescription_${product.id}`}
                        className="store-product-item-name"
                      >
                        {product.description}
                      </div>
                      <div
                        className="store-product-selection-menu"
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <FormControl
                          style={{ ...storyBookStyles, width: '80px' }}
                        >
                          <InputLabel style={{ fontSize: '12px' }}>
                            QTY
                          </InputLabel>
                          <Select
                            label="QTY"
                            value={quantities[product.id] || 1}
                            onChange={(e) =>
                              handleQuantityChange(product.id, e.target.value)
                            }
                            style={{ height: '50px' }}
                            className="store-qty-product-selection"
                          >
                            <MenuItem
                              key="one-qty"
                              value={1}
                              className="store-menu-item"
                            >
                              1
                            </MenuItem>
                            <MenuItem
                              key="two-qty"
                              value={2}
                              className="store-menu-item"
                            >
                              2
                            </MenuItem>
                            <MenuItem
                              key="three-qty"
                              value={3}
                              className="store-menu-item"
                            >
                              3
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="secondary"
                          id={`addToCartBtn_${product.id}`}
                          className="demoBtn store-add-to-cart-btn"
                          onClick={() => addToCart(product)}
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col span={7} className="store-cart-col">
              <div className="shopping-cart-card">
                <div className="store-shopping-cart-label">Shopping Cart</div>
                <hr />
                <div className="store-cart-items">
                  {cart.length === 0 ? (
                    <div className="store-empty-cart-message">
                      Your shopping cart is empty.
                    </div>
                  ) : (
                    cart.map((item, index) => (
                      <div key={item.id}>
                        <div
                          className="store-cart-item"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '5px 0'
                          }}
                        >
                          <div>{item.fancyName}</div>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Button
                              variant="tertiary"
                              onClick={() => decrementQuantity(item.id)}
                            >
                              <RemoveCircleOutlineIcon />
                            </Button>
                            <span style={{ margin: '0 10px' }}>
                              <TextField
                                value={item.quantity}
                                disabled
                                className="store-qty"
                              />
                            </span>
                            <Button
                              onClick={() => incrementQuantity(item.id)}
                              variant="tertiary"
                            >
                              <AddCircleOutlineIcon />
                            </Button>
                          </div>
                        </div>
                        {cart.length > 1 && index < cart.length - 1 && <hr />}
                      </div>
                    ))
                  )}
                </div>
                <hr />
                <div
                  className="store-total-div"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: '500'
                  }}
                >
                  <span>Total</span>
                  <span>
                    $
                    {cart
                      .reduce(
                        (total, item) =>
                          total +
                          parseFloat(
                            item.description.replace(/[^0-9.-]+/g, '')
                          ) *
                            item.quantity,
                        0
                      )
                      .toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="shopping-cart-card store-address-card">
                <div className="store-add-panel">
                  <div>
                    <b>Shipping Address</b>
                  </div>
                  <div>John Doe</div>
                  <div className="store-add-item">123 Main St.</div>
                  <div className="store-add-item">San Jose, CA 95128</div>
                </div>
              </div>
              <div className="shopping-cart-card store-address-card">
                {showInfo && storeType === 'sdk' && (
                  <Alert
                    severity="info"
                    className="store-info"
                    sx={{
                      '& .MuiAlert-icon': {
                        color: '#3D5CCF'
                      }
                    }}
                    onClose={handleInfoClose}
                  >
                    <div className="sdk-txt">Snap SDK Integration</div>
                    <div className="sdk-txt-secondary">
                      The Snap Checkout button is designed to be a payment
                      option for your website. When a customer clicks the
                      button, a pop up window launches a customer application.
                      When the customer completes the application, the window
                      closes. Please refer the{' '}
                      <a
                        className="snap-link"
                        target="_blank"
                        href="https://developer.snapfinance.com/javascript-sdk/"
                        rel="noreferrer"
                      >
                        developer guide
                      </a>
                      .
                    </div>
                  </Alert>
                )}
                {showInfo && storeType === 'lto' && (
                  <Alert
                    severity="info"
                    className="store-info"
                    sx={{
                      '& .MuiAlert-icon': {
                        color: '#3D5CCF'
                      }
                    }}
                    onClose={handleInfoClose}
                  >
                    <div className="sdk-txt">Direct Lease To Own</div>
                    <div className="sdk-txt-secondary">
                      This RESTful API empowers customers and partners to build
                      custom applications that interact with Snap Finance.
                      Please refer the{' '}
                      <a
                        className="snap-link"
                        target="_blank"
                        href="https://platform.api-spec.snapfinance.com/"
                        rel="noreferrer"
                      >
                        developer guide
                      </a>
                      .
                    </div>
                  </Alert>
                )}
              </div>
              <div className="store-review-card">
                <Button
                  className="store-review-btn"
                  onClick={() => setIsModalVisible(true)}
                  disabled={cart.length === 0}
                >
                  Review and Checkout
                </Button>
              </div>
            </Col>
          </Row>
        </div>
        <SnapDialog
          data-testid="checkout-partner-modal"
          open={isModalVisible && storeType === 'sdk'}
          onClose={handleCancel}
        >
          <Checkout
            isModalVisible={isModalVisible && storeType === 'sdk'}
            handleCancel={handleCancel}
            cart={cart}
            salesTax={salesTax}
            shipping={shipping}
            discount={discount}
            env={env}
            token={token}
            currency={currency}
            audience={audience}
          />
        </SnapDialog>
        <SnapDialog
          data-testid="checkout-partner-modal"
          open={isModalVisible && storeType === 'lto'}
          onClose={handleCancel}
        >
          <LtoCheckout
            isModalVisible={isModalVisible && storeType === 'lto'}
            handleCancel={handleCancel}
            cart={cart}
            salesTax={salesTax}
            shipping={shipping}
            discount={discount}
            env={env}
            token={token}
            currency={currency}
            audience={audience}
            openSnackbar={handleOpenSnackbar}
          />
        </SnapDialog>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success">
            Your application has been received!
          </Alert>
        </Snackbar>
      </Content>
    </Layout>
  );
}

export default ProductsPage;
